import { palette, colors, alpha } from '@pinmerge/tokens';

export const styles = /* css */ `
  :host, * { box-sizing: border-box; }

  /* --- FAB (single circular launcher) + popover menu ---
     Replaces the old three-pill launcher row. One click → menu toggles.
     No hover trigger so the interaction works identically on touch. */
  .fab {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    background: ${palette.zinc900};
    color: ${palette.zinc50};
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    box-shadow: 0 8px 20px ${alpha(palette.black, 0.45)};
    transition: transform 120ms ease, background 120ms ease, box-shadow 120ms ease;
    z-index: 2147483646;
  }
  .fab:hover { transform: translateY(-1px); background: ${palette.zinc800}; }
  .fab--active {
    background: ${colors.brand.primary};
    box-shadow: 0 0 0 4px ${alpha(palette.teal500, 0.2)}, 0 8px 20px ${alpha(palette.black, 0.45)};
  }
  /* Comment-count badge — top-right corner of the FAB. */
  .fab__badge {
    position: absolute;
    top: -2px; right: -2px;
    min-width: 20px; height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: ${colors.brand.primary};
    color: ${palette.zinc50};
    font: 600 11px/20px ui-sans-serif, system-ui, sans-serif;
    text-align: center;
    box-shadow: 0 2px 6px ${alpha(palette.black, 0.4)};
    pointer-events: none;
  }
  .fab--active .fab__badge {
    background: ${palette.zinc900};
  }
  /* Pulsing dot bottom-right when local-only comments are still syncing. */
  .fab__sync {
    position: absolute;
    bottom: 4px; right: 4px;
    width: 8px; height: 8px;
    border-radius: 50%;
    background: ${colors.state.pending};
    box-shadow: 0 0 0 2px ${palette.zinc900};
    animation: fab-sync-pulse 1400ms ease-in-out infinite;
  }
  @keyframes fab-sync-pulse {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.35; }
  }

  /* Prototype-version badge — sits left of the FAB, surfaces which iteration
     of the prototype the reviewer is currently viewing. Driven by ?pinmerge_v=
     in the URL (or the project default). Hidden when the project has no
     versions configured (activeVersion === null). */
  .fab__version-badge {
    position: fixed;
    bottom: 28px;
    right: 84px;
    max-width: 160px;
    padding: 4px 10px;
    border-radius: 999px;
    background: ${palette.zinc900};
    color: ${palette.zinc50};
    border: 1px solid ${palette.zinc800};
    font: 600 11px/1.4 ui-sans-serif, system-ui, sans-serif;
    letter-spacing: 0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-shadow: 0 4px 12px ${alpha(palette.black, 0.4)};
    pointer-events: none;
    z-index: 2147483646;
  }

  /* Menu popover — anchored above the FAB, right-aligned. */
  .fab-menu {
    position: fixed;
    bottom: 88px;
    right: 20px;
    min-width: 240px;
    background: ${palette.zinc950};
    color: ${palette.zinc50};
    border: 1px solid ${palette.zinc800};
    border-radius: 12px;
    padding: 6px;
    box-shadow: 0 20px 50px ${alpha(palette.black, 0.5)};
    font: 500 13px/1.4 ui-sans-serif, system-ui, sans-serif;
    z-index: 2147483646;
    animation: fab-menu-in 120ms ease-out;
  }
  @keyframes fab-menu-in {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fab-menu__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    background: transparent;
    color: ${palette.zinc50};
    border: none;
    border-radius: 8px;
    text-align: left;
    cursor: pointer;
    font: inherit;
  }
  .fab-menu__item:hover { background: ${palette.zinc900}; }
  .fab-menu__item svg { color: ${palette.zinc400}; flex-shrink: 0; }
  .fab-menu__item:hover svg { color: ${palette.zinc50}; }
  .fab-menu__item > span {
    flex: 1; min-width: 0;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .fab-menu__item--primary {
    background: ${alpha(palette.teal500, 0.12)};
    color: ${colors.brand.accentLight};
  }
  .fab-menu__item--primary svg { color: ${colors.brand.accentLight}; }
  .fab-menu__item--primary:hover {
    background: ${alpha(palette.teal500, 0.2)};
    color: ${colors.brand.accentLighter};
  }
  .fab-menu__item--primary:hover svg { color: ${colors.brand.accentLighter}; }
  .fab-menu__item--danger { color: ${palette.zinc400}; }
  .fab-menu__item--danger:hover {
    background: ${alpha(palette.teal500, 0.12)};
    color: ${colors.brand.accentLight};
  }
  .fab-menu__item--danger:hover svg { color: ${colors.brand.accentLight}; }
  .fab-menu__kbd {
    font: 500 10px/1 ui-monospace, monospace;
    color: ${palette.zinc500};
    background: ${palette.zinc900};
    padding: 3px 6px;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .fab-menu__sep {
    height: 1px;
    background: ${palette.zinc900};
    margin: 4px 6px;
  }
  /* Read-only label row (e.g. "logged in as foo@…"). Looks like a menu item but
     not clickable. */
  .fab-menu__label {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    color: ${palette.zinc400};
    font: 500 12px/1.4 ui-sans-serif, system-ui, sans-serif;
  }
  .fab-menu__label svg { color: ${palette.zinc500}; flex-shrink: 0; }
  .fab-menu__label > span {
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  @media (max-width: 480px) {
    .fab { width: 52px; height: 52px; }
    .fab-menu { right: 12px; bottom: 76px; min-width: 260px; }
  }

  /* --- modal (registration / future flows) --- */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: ${colors.overlay.backdrop};
    z-index: 2147483647;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    animation: pin-in 120ms ease-out;
  }
  .modal {
    width: 100%;
    max-width: 380px;
    background: ${palette.zinc950};
    color: ${palette.zinc50};
    border: 1px solid ${palette.zinc800};
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    font: 500 13px/1.4 ui-sans-serif, system-ui, sans-serif;
    box-shadow: 0 24px 60px ${alpha(palette.black, 0.55)};
  }
  .modal__head {
    display: flex; align-items: center; justify-content: space-between;
    gap: 8px;
  }
  .modal__title {
    margin: 0; font-size: 14px; font-weight: 600;
  }
  .modal__body {
    margin: 0; font-size: 12px; line-height: 1.5; color: ${palette.zinc400};
  }
  .modal__body code {
    font-family: ui-monospace, monospace;
    font-size: 11px;
    color: ${palette.zinc300};
    background: ${palette.zinc900};
    padding: 1px 5px;
    border-radius: 4px;
  }
  .modal__actions {
    display: flex; align-items: center; justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
  }

  /* --- hint banner --- */
  .hint {
    position: fixed; top: 16px; left: 50%; transform: translateX(-50%);
    padding: 8px 14px; border-radius: 999px;
    background: ${palette.zinc900}; color: ${palette.zinc50};
    font: 500 12px/1.4 ui-sans-serif, system-ui, sans-serif;
    box-shadow: 0 4px 12px ${alpha(palette.black, 0.25)};
  }
  .hint kbd {
    background: ${palette.zinc700}; padding: 1px 6px; border-radius: 4px;
    font-family: ui-monospace, monospace; font-size: 11px;
  }

  /* --- tab-auth banner ---
     Status surface for the new-tab login flow. Sits above the FAB so the
     reviewer can see what's happening without the FAB obscuring the message.
     Visible while polling, while an error sits unresolved, and during the
     brief moment between Continue and the polling endpoint draining. */
  .tab-auth {
    position: fixed;
    bottom: 88px;
    right: 20px;
    width: min(320px, calc(100vw - 40px));
    padding: 14px;
    border-radius: 12px;
    background: ${palette.zinc900};
    color: ${palette.zinc50};
    border: 1px solid ${palette.zinc800};
    box-shadow: 0 12px 28px ${alpha(palette.black, 0.45)};
    font: 500 13px/1.45 ui-sans-serif, system-ui, sans-serif;
    z-index: 2147483646;
    display: grid; gap: 8px;
  }
  .tab-auth--error { border-color: ${alpha(palette.red500, 0.5)}; }
  .tab-auth__title { font-weight: 600; font-size: 13px; }
  .tab-auth__body { color: ${palette.zinc300}; font-size: 12px; }
  .tab-auth__actions {
    display: flex; align-items: center; gap: 12px; margin-top: 4px;
  }
  .tab-auth__btn {
    background: ${colors.brand.primary}; color: ${palette.zinc50};
    border: none; border-radius: 8px; padding: 6px 12px;
    font: 600 12px/1.4 ui-sans-serif, system-ui, sans-serif;
    cursor: pointer;
  }
  .tab-auth__btn:hover { background: ${colors.brand.primaryHover}; }
  .tab-auth__link {
    background: transparent; border: none; padding: 0;
    color: ${palette.zinc300}; font: 500 12px/1.4 ui-sans-serif, system-ui, sans-serif;
    text-decoration: underline; cursor: pointer;
  }
  .tab-auth__link:hover { color: ${palette.zinc50}; }

  /* --- comment markers (numbered round pins on the page) ---
     The marker button is the AvatarSlot's outer shell: it owns the pin shape
     (border-radius 50% 50% 50% 4px) and clips an AvatarSlot inside via
     overflow:hidden. The slot fills 100%/100% so the SVG covers the whole pin. */
  .marker {
    position: fixed;
    width: 28px; height: 28px;
    border-radius: 50% 50% 50% 4px;
    background: ${colors.pin.own};
    color: ${palette.white};
    border: 2px solid ${palette.white};
    cursor: pointer;
    padding: 0; margin: 0;
    display: flex; align-items: center; justify-content: center;
    font: 600 12px/1 ui-sans-serif, system-ui, sans-serif;
    box-shadow: 0 4px 10px ${alpha(palette.black, 0.25)};
    transition: transform 120ms ease, box-shadow 120ms ease;
    z-index: 2147483640;
    overflow: hidden;
  }
  .marker:hover { transform: scale(1.1); box-shadow: 0 6px 14px ${alpha(palette.black, 0.3)}; }
  .marker--active { transform: scale(1.15); box-shadow: 0 0 0 4px ${alpha(colors.pin.own, 0.25)}, 0 4px 10px ${alpha(palette.black, 0.25)}; }
  /* Marker variant of pin__avatar: fills the button edge-to-edge (no inner
     circle), so the parent's pin border-radius clips the SVG into the pin
     shape. Background colour comes from .marker itself. */
  .pin__avatar--marker {
    width: 100%; height: 100%;
    border-radius: 0;
    background: transparent !important;
  }
  .pin__avatar--marker img {
    border-radius: 0;
  }

  /* Comments left by other reviewers — violet vs. the owner's orange so reviewers
     can tell at a glance which pins they own and which they can only read. Pins are
     off-theme (they sit on arbitrary prototypes), hence their own pin.* tokens. */
  .marker--other { background: ${colors.pin.other}; }
  .marker--other.marker--active {
    box-shadow: 0 0 0 4px ${alpha(colors.pin.other, 0.25)}, 0 4px 10px ${alpha(palette.black, 0.25)};
  }

  /* Pin hover tooltip — small dark card under the marker. Pointer-events:none
     so hovering the tooltip itself doesn't keep it alive past the marker leave. */
  .pin-tooltip {
    position: fixed;
    max-width: 280px;
    background: ${palette.zinc900};
    color: ${palette.zinc50};
    border: 1px solid ${palette.zinc800};
    border-radius: 8px;
    padding: 8px 10px;
    font: 500 12px/1.4 ui-sans-serif, system-ui, sans-serif;
    box-shadow: 0 8px 24px ${alpha(palette.black, 0.4)};
    pointer-events: none;
    z-index: 2147483641;
    display: flex; flex-direction: column; gap: 4px;
    animation: pin-in 100ms ease-out;
  }
  .pin-tooltip__head {
    display: flex; align-items: center; gap: 6px;
  }
  .pin-tooltip__name {
    font-weight: 600; font-size: 12px; color: ${palette.zinc50};
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    flex: 1; min-width: 0;
  }
  .pin-tooltip__time {
    font-size: 10px; color: ${palette.zinc500};
    flex-shrink: 0;
  }
  .pin-tooltip__text {
    font-size: 12px; color: ${palette.zinc300};
    overflow: hidden; text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    word-break: break-word;
  }
  .pin-tooltip__text--locked {
    display: flex; align-items: center; gap: 4px;
    color: ${palette.zinc400}; font-style: italic;
  }
  .pin-tooltip__text--locked svg {
    flex-shrink: 0;
  }
  .pin-tooltip__replies {
    font-size: 11px; color: ${palette.zinc500};
  }
  .pin__avatar--xs {
    width: 18px; height: 18px;
    font-size: 9px;
  }

  /* --- inline pin (Figma-style popover) --- */
  .pin {
    position: fixed;
    width: 320px;
    background: ${palette.zinc950};
    color: ${palette.zinc50};
    border: 1px solid ${palette.zinc800};
    border-radius: 10px;
    box-shadow: 0 16px 40px ${alpha(palette.black, 0.45)};
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    font: 500 13px/1.4 ui-sans-serif, system-ui, sans-serif;
    animation: pin-in 120ms ease-out;
    transform-origin: top left;
    z-index: 2147483645;
  }
  .pin--tr { transform-origin: top right; }
  .pin--bl { transform-origin: bottom left; }
  .pin--br { transform-origin: bottom right; }

  @keyframes pin-in {
    from { opacity: 0; transform: scale(0.96); }
    to   { opacity: 1; transform: scale(1); }
  }

  /* Top chrome bar (view state only) — Comment title + close button. */
  .pin__chrome {
    display: flex; align-items: center; justify-content: space-between;
    gap: 8px;
    margin: -2px -2px 2px -2px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${palette.zinc850};
  }
  .pin__chrome-title {
    font: 600 12px/1 ui-sans-serif, system-ui, sans-serif;
    text-transform: none;
    letter-spacing: 0.01em;
    color: ${palette.zinc300};
  }

  /* Login wall shown inside the pin popup when the viewer is signed out. The
     pin header (title + close) still renders above; this fills the rest. */
  .pin__locked {
    display: flex; flex-direction: column; align-items: center;
    gap: 8px; padding: 16px 12px 8px;
    text-align: center;
  }
  .pin__locked-icon {
    width: 40px; height: 40px;
    border-radius: 50%;
    background: ${alpha(colors.pin.own, 0.12)};
    color: ${colors.pin.own};
    display: flex; align-items: center; justify-content: center;
  }
  .pin__locked-title {
    font: 600 13px/1.3 ui-sans-serif, system-ui, sans-serif;
    color: ${palette.zinc50};
  }
  .pin__locked-sub {
    font: 400 12px/1.4 ui-sans-serif, system-ui, sans-serif;
    color: ${palette.zinc400};
    max-width: 240px;
  }
  .pin__locked .btn--primary {
    margin-top: 4px;
  }

  /* Comment row container inside the view popup — wraps avatar/name/time + body
     so the per-comment hover icons (added in the next step) can be scoped to a
     single hover region. */
  .pin__comment {
    display: flex; flex-direction: column; gap: 6px;
  }
  .pin__comment-head {
    display: flex; align-items: center; gap: 8px;
    min-width: 0;
  }

  .pin__head {
    display: flex; align-items: center; justify-content: space-between;
    gap: 8px;
  }
  .pin__author {
    display: flex; align-items: center; gap: 8px;
    min-width: 0;
  }
  .pin__avatar {
    width: 26px; height: 26px;
    border-radius: 50%;
    background: linear-gradient(135deg, ${colors.pin.own}, ${colors.pin.ownHover});
    color: ${palette.white};
    font-size: 12px; font-weight: 600;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .pin__avatar--other {
    background: linear-gradient(135deg, ${colors.pin.other}, ${colors.pin.otherHover});
  }
  /* When an avatar SVG is present we fill the circle with it; the parent's
     inline background-colour (avatar_bg) shows through any transparent regions
     of the SVG, matching the marker's appearance. */
  .pin__avatar img {
    width: 100%; height: 100%;
    border-radius: inherit;
    object-fit: cover;
    display: block;
  }
  .pin__author-meta {
    display: flex; flex-direction: column;
    min-width: 0; line-height: 1.2;
  }
  .pin__author-name {
    font-size: 13px; color: ${palette.zinc50};
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    min-width: 0;
  }
  .pin__time {
    font-size: 11px; color: ${palette.zinc500};
    flex-shrink: 0;
  }
  .pin__close {
    background: transparent; border: none;
    color: ${palette.zinc500}; cursor: pointer;
    width: 22px; height: 22px;
    border-radius: 4px;
    font-size: 18px; line-height: 1;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .pin__close:hover { background: ${palette.zinc800}; color: ${palette.zinc50}; }

  /* Action icons (Smile + MoreHorizontal) at the top-right of a comment row.
     Always visible — anchored to the right of the head row via margin-left auto. */
  .comment-actions {
    display: inline-flex; align-items: center; gap: 2px;
    margin-left: auto;
    position: relative;
  }
  .comment-action-btn {
    background: transparent; border: 1px solid transparent;
    width: 24px; height: 22px;
    border-radius: 6px;
    color: ${palette.zinc300};
    font-size: 14px; line-height: 1;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    padding: 0;
  }
  .comment-action-btn:hover {
    background: ${palette.zinc800};
    border-color: ${palette.zinc700};
    color: ${palette.zinc50};
  }

  /* Per-comment menu popover — anchored under the … action button. */
  .comment-menu {
    position: absolute;
    top: 100%; right: 0;
    margin-top: 4px;
    min-width: 140px;
    background: ${palette.zinc950};
    border: 1px solid ${palette.zinc800};
    border-radius: 8px;
    box-shadow: 0 8px 24px ${alpha(palette.black, 0.4)};
    padding: 4px;
    z-index: 6;
    display: flex; flex-direction: column;
  }
  .comment-menu__item {
    display: block;
    width: 100%;
    text-align: left;
    background: transparent; border: none;
    padding: 6px 10px;
    border-radius: 4px;
    font: 500 12px/1.2 ui-sans-serif, system-ui, sans-serif;
    color: ${palette.zinc300};
    cursor: pointer;
  }
  .comment-menu__item:hover { background: ${palette.zinc900}; color: ${palette.zinc50}; }
  .comment-menu__item--danger { color: ${colors.brand.accentLight}; }
  .comment-menu__item--danger:hover { background: ${alpha(palette.teal500, 0.12)}; color: ${colors.brand.accentLighter}; }
  .comment-menu__check {
    display: inline-flex; align-items: center; gap: 4px;
    color: ${colors.state.success};
  }

  .pin__body {
    color: ${palette.zinc300};
    font-size: 13px; line-height: 1.5;
    white-space: pre-wrap; word-break: break-word;
    cursor: text;
    padding: 4px 2px;
    border-radius: 4px;
    user-select: text;
  }
  .pin__body:hover { background: ${palette.zinc900}; }

  /* Cropped element-area thumbnail shown in the open pin view. Clicking opens
     the full-resolution image in a new tab. */
  .pin__thumb {
    display: block;
    border: 1px solid ${palette.zinc800};
    border-radius: 6px;
    overflow: hidden;
    background: ${palette.zinc900};
    transition: border-color 120ms ease;
    line-height: 0;
  }
  .pin__thumb:hover { border-color: ${palette.zinc700}; }
  .pin__thumb img {
    display: block;
    width: 100%;
    max-height: 160px;
    object-fit: contain;
  }

  .input, .textarea {
    background: ${palette.zinc900};
    color: ${palette.zinc50};
    border: 1px solid ${palette.zinc800};
    border-radius: 6px;
    padding: 8px 10px;
    font: inherit;
    width: 100%;
    outline: none;
    transition: border-color 120ms ease;
  }
  .input { font-size: 12px; }
  .input:focus, .textarea:focus { border-color: ${palette.zinc600}; }
  .textarea { resize: none; min-height: 64px; font-size: 13px; overflow: hidden; }

  .error {
    color: ${colors.state.error}; font-size: 11px;
  }

  /* Reactions row: existing reactions render as inline pills (Slack-style),
     plus a hover-revealed [😊+] trigger that opens the full emoji picker. */
  .reactions {
    display: flex; flex-wrap: wrap; align-items: center; gap: 4px;
    margin-top: 2px;
    position: relative;
  }
  .reaction {
    display: inline-flex; align-items: center; gap: 3px;
    padding: 3px 7px;
    border: 1px solid ${palette.zinc800};
    border-radius: 999px;
    background: ${palette.zinc900};
    color: ${palette.zinc300};
    font: 500 12px/1 ui-sans-serif, system-ui, sans-serif;
    cursor: pointer;
    transition: border-color 80ms ease, background 80ms ease, transform 80ms ease, opacity 80ms ease;
  }
  .reaction:hover { border-color: ${palette.zinc600}; background: ${palette.zinc800}; transform: translateY(-1px); }
  .reaction--mine {
    border-color: ${colors.brand.primary};
    background: ${alpha(palette.teal500, 0.15)};
  }
  .reaction__emoji { font-size: 14px; line-height: 1; }
  .reaction__count { font-size: 11px; color: ${palette.zinc400}; }
  .reaction--mine .reaction__count { color: ${colors.brand.accentLight}; }

  /* Picker popover — anchored under the reactions row. */
  .reaction-picker {
    position: absolute;
    top: 100%; left: 0;
    margin-top: 4px;
    display: flex; gap: 2px;
    padding: 4px;
    background: ${palette.zinc950};
    border: 1px solid ${palette.zinc800};
    border-radius: 8px;
    box-shadow: 0 8px 24px ${alpha(palette.black, 0.4)};
    z-index: 5;
  }
  .reaction-picker__btn {
    width: 28px; height: 28px;
    border: none; border-radius: 6px;
    background: transparent;
    font-size: 18px; line-height: 1;
    cursor: pointer;
    transition: background 60ms ease, transform 60ms ease;
  }
  .reaction-picker__btn:hover { background: ${palette.zinc800}; transform: scale(1.15); }

  /* ─── Replies ─────────────────────────────────────────────────────── */
  /* Top-level comment and replies share the .pin__comment shape — replies
     render as a flat list under the parent with the same DOM structure. */
  .replies {
    display: flex; flex-direction: column; gap: 12px;
  }
  .btn--small { padding: 4px 8px; font-size: 11px; }

  .reply-composer {
    display: flex; flex-direction: column; gap: 6px;
    margin-top: 2px;
  }
  .reply-composer .textarea { font-size: 12px; min-height: 48px; }

  /* Wraps a textarea + an inline send button (anchored bottom-right). */
  .composer-input { position: relative; }
  .composer-input .textarea { padding-right: 36px; }
  .composer-send {
    position: absolute;
    right: 6px;
    bottom: 6px;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 4px;
    background: ${colors.brand.primary};
    color: ${palette.zinc50};
    display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer; padding: 0;
    transition: background 120ms ease;
  }
  .composer-send:hover:not(:disabled) { background: ${colors.brand.primaryHover}; }
  .composer-send:disabled {
    background: ${palette.zinc800}; color: ${palette.zinc600}; cursor: not-allowed;
  }

  .pin__actions {
    display: flex; align-items: center; justify-content: flex-end;
    gap: 8px;
    margin-top: 2px;
  }

  .btn {
    padding: 6px 12px; border-radius: 6px; border: none;
    font: 500 12px/1 ui-sans-serif, system-ui, sans-serif;
    cursor: pointer;
    transition: background 120ms ease, color 120ms ease;
    white-space: nowrap;
  }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn--primary { background: ${colors.brand.primary}; color: ${palette.zinc50}; }
  .btn--primary:hover:not(:disabled) { background: ${colors.brand.primaryHover}; }
  .btn--ghost { background: transparent; color: ${palette.zinc400}; }
  .btn--ghost:hover:not(:disabled) { background: ${palette.zinc900}; color: ${palette.zinc50}; }
  .btn--danger:hover:not(:disabled) { background: ${alpha(palette.teal500, 0.12)}; color: ${colors.brand.accentLight}; }

  /* ─── Mobile (≤ 640px): full-screen pin overlay + touch-friendly polish ──
     The popover JS still computes a (left, top) — we override with !important
     so the pin fills the viewport edge-to-edge. Inline scrolling, no border
     radius, no animation flourish that fights iOS rendering.

     visualViewport-aware keyboard handling: App.tsx writes the visible
     viewport height into --pinmerge-vv-h on the host element so the overlay
     can shrink when the on-screen keyboard pushes up. Falls back to 100dvh
     on browsers without visualViewport. */
  @media (max-width: 640px) {
    .pin {
      left: 0 !important;
      right: 0 !important;
      top: 0 !important;
      bottom: 0 !important;
      width: auto !important;
      max-width: none !important;
      max-height: none !important;
      height: var(--pinmerge-vv-h, 100dvh);
      overflow-y: auto;
      border-radius: 0 !important;
      border: none !important;
      box-shadow: none !important;
      padding: 14px 16px 16px;
      animation: pin-fade-in 160ms ease-out;
      transform-origin: center !important;
      -webkit-overflow-scrolling: touch;
    }

    /* No hover previews on touch — they only flicker on tap-and-drag. */
    .pin-tooltip { display: none !important; }

    /* Tap-target: marker visual stays 28px, but the click area expands to
       ≥44px via a transparent ::after pseudo-element. */
    .marker {
      /* Centring offsets baked in JS assume 28×28; don't change the box
         model, only add invisible reach. */
      position: fixed;
    }
    .marker::after {
      content: '';
      position: absolute;
      inset: -8px; /* 28 + 16 = 44 */
      border-radius: inherit;
    }

    /* Textareas: 16px prevents iOS auto-zoom on focus. Composer-send button
       gets a 44×44 hit area (visual stays small via padding). */
    .textarea {
      font-size: 16px !important;
    }
    .composer-send {
      min-width: 44px !important;
      min-height: 44px !important;
    }
    .pin__close,
    .pin__icon,
    .pin__menu-item {
      min-width: 44px;
      min-height: 44px;
    }

    /* Email metadata in pin head clips on narrow screens — hide on mobile,
       desktop hover still shows it via title attr if added. */
    .pin__author-email,
    .pin__verified-badge {
      display: none;
    }
  }

  @keyframes pin-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;
